/** COMMON IMPORTS */
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BrowserRouter as Router, Switch, Route, useHistory } from "react-router-dom";
import PrivateRoute from "./components/reuseableComponents/auth/PrivateRoute";
import { UserContext } from "./components/reuseableComponents/auth/UserContext";
import { EnvironmentProvider } from "./configuration/EnvironmentContext";
import URL404 from "./components/reuseableComponents/URL404";
import Footer from "./components/reuseableComponents/navigation/Footer";

/** ROUTE IMPORTS */
import { archiveRoutes } from "./routes/archiveRoutes";
import { ddwAdminRoutes } from "./routes/ddwAdminRoutes.js";
import { enforcementRoutes } from "./routes/enforcementRoutes";
import { publicRoutes } from "./routes/publicRoutes";
import { portalRoutes } from "./routes/portalRoutes";

/** UTAH DESIGN SYSTEM IMPORTS */
import { appConfig } from "./configuration/AppConfig";
import { childrenMenuTypes, setUtahHeaderSettings } from "@utahdts/utah-design-system-header";
import logo from "./images/DDW_LOGOS/Logo-DDW.svg"
import "@utahdts/utah-design-system/css";
import "@utahdts/utah-design-system-header/css";
import "./css/utahDesign.css";

/** Dynamic Data for Menu Items */
import { useQuery } from "@apollo/client";
import { GET_MENU_ITEMS } from "./queries/menuQrys";

/** Sidebar Imports */
import SidePanel from "./components/reuseableComponents/SidePanel.js";
import WaterSystemSearchModal from "./components/reuseableComponents/WaterSystemSearchModal";
import { WaterSystemSearchModalProvider } from "./components/reuseableComponents/WaterSystemSearchModalContext.js";

/** Status Bar Imports */
// import FakeLink from "./components/reuseableComponents/FakeLink";
import StatusBar from "./components/reuseableComponents/StatusBar";
import { HoverProvider } from "./context/HoverContext.js";

/**TODO:
 * remove Header.js once we finish refacturing code.
 */
function HeaderComponent({ setPanelMenu, setMenuItemsArray, providerUser, logout, user, menuItemsData, refetchMenuItems, loading, error, menuItemsArray, panelMenu, setAuthCookie }) {
  const history = useHistory();
  const hasRefetched = useRef(false);

  const showSidePanel = useCallback((menuItemCode) => {
    const sidePanelContainer = document.querySelector(".side-panel-container");
    if (sidePanelContainer) {
      sidePanelContainer.classList.remove("hidden");
      sidePanelContainer.classList.add("visible");
    }
    setPanelMenu(menuItemCode);
  }, [setPanelMenu]);

  // Add this effect to set sidePanelMenu based on URL when HeaderComponent renders
  useEffect(() => {
    const pathname = window.location.pathname;
    const sidePanelVisible = document.querySelector('.side-panel-container')?.classList.contains('visible');
    if (
      pathname === "/" ||
      pathname === "/public-index" ||
      sidePanelVisible
    ) {
      // Don't set sidePanelMenu
      return;
    }
    if (pathname.includes("home")) {
      setPanelMenu("HOME");
      showSidePanel("HOME");
      return;
    }
    if (pathname.includes("ddw-admin")) {
      setPanelMenu("DDW_ADMIN");
      showSidePanel("DDW_ADMIN");
      return;
    }
    if (pathname.includes("portal")) {
      setPanelMenu("PORTAL");
      showSidePanel("PORTAL");
      return;
    }
    if (pathname.includes("public")) {
      setPanelMenu("PUBLIC");
      showSidePanel("PUBLIC");
      return;
    }
  }, [setPanelMenu, showSidePanel]);

  

  const getChildMenuItems = useCallback((menuItem) => {
    return menuItem.children.map((child) => {
      const childMenu = (child.children && child.children.length > 0)
        ? getChildMenuItems(child)
        : [];
      const item = {
        title: child.label.toUpperCase(),
        isSelected: false,
      };
      if (childMenu.length > 0) {
        item.actionMenu = childMenu;
        item.childrenMenuType = childrenMenuTypes.DROPDOWN;
      } else {
        item.actionUrl = {
          url: child.url_route,
        };
      }
      return item;
    });
  }, []);

  useEffect(() => {
    if (loading || error) return;
    let items = [];
    if (menuItemsData && menuItemsData.hasOwnProperty("getMenuItems")) {
      menuItemsData.getMenuItems.sections.forEach((menuItem) => {
        if (menuItem.children.length > 0) {
          items.push({
            id: menuItem.code,
            title: menuItem.label.toUpperCase(),
            isSelected: (panelMenu === menuItem.code),
            actionFunction: () => {
              showSidePanel(menuItem.code);
            },
            children: menuItem.children
          });
        } else {
          items.push({
            actionUrl: {
              url: menuItem.url_route,
            },
            title: menuItem.label.toUpperCase(),
            isSelected: (panelMenu === menuItem.code),
          });
        }
      });
    }
    if (JSON.stringify(items) !== JSON.stringify(menuItemsArray)) {
      setMenuItemsArray(items);
    }
  }, [loading, error, menuItemsData, setPanelMenu, setMenuItemsArray, user, showSidePanel, menuItemsArray, panelMenu]);

  useEffect(() => {
    setUtahHeaderSettings({
      title: "Waterlink",
      utahId: {
        currentUser: providerUser?.user?.firstName ? {
          first: providerUser.user.firstName,
          last: providerUser.user.lastName,
          email: providerUser.user.email,
          authenticated: sessionStorage?.auth ? JSON.parse(sessionStorage.auth) : false
        } : null,
        onSignIn: () => {
          setAuthCookie("v2Authenticated", "true");
          window.location.assign(`${appConfig.authEndpoint}${appConfig.authParams}`);
          refetchMenuItems();
        },
        onSignOut: () => {
          logout(history, refetchMenuItems);
        },
      },
      logo: {
        htmlString: '<img src="' + logo + '" id="ddw-logo" alt="Logo-DDW" />'
      },
      mainMenu: {
        menuItems: Array.isArray(menuItemsArray) ? menuItemsArray : [],
      },
      footer: {
        showHorizontalRule: true,
      },
    });
  }, [history, providerUser, logout, refetchMenuItems, menuItemsArray, setAuthCookie]);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (token && !hasRefetched.current) {
      refetchMenuItems();
      hasRefetched.current = true;
    }
    if (!token) {
      hasRefetched.current = false;
    }
  }, [refetchMenuItems]);

  return null;
}

const App = () => {
  const [user, setUser] = useState(
    sessionStorage.getItem("user") ? JSON.parse(sessionStorage.user) : JSON.parse(sessionStorage.defaultUser)
  );

  const [showSystemSelectModal, setShowSystemSelectModal] = useState(false);
  const [panelMenu, setPanelMenu] = useState("");
  const [menuItemsArray, setMenuItemsArray] = useState([]);
  const [selectedWaterSystem, setSelectedWaterSystem] = useState(
    sessionStorage.getItem("selectedWaterSystem")
      ? JSON.parse(sessionStorage.getItem("selectedWaterSystem"))
      : null
  );

  const providerUser = useMemo(() => ({ user, setUser }), [user, setUser]);

  const setAuthCookie = useCallback((name, value) => {
    const expires = new Date(Date.now() + 10 * 60 * 60 * 1000).toUTCString(); // 10 hours
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }, []);

  const destroyAuthCookie = (name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  };

  const logout = (history, refetch) => {
    destroyAuthCookie("v2Authenticated");
    destroyAuthCookie("refresh_token");
    sessionStorage.removeItem("user");
    sessionStorage.removeItem("auth");
    sessionStorage.removeItem("route");
    sessionStorage.removeItem("token");
    history.push("/");
    setUser({});
    refetch();
  };

  // Move GET_MENU_ITEMS query to App
  const { loading, error, data, refetch } = useQuery(GET_MENU_ITEMS);

  return (
    <EnvironmentProvider>
      <UserContext.Provider value={providerUser}>
        <WaterSystemSearchModalProvider>
          <HoverProvider>
            <Router>
              <HeaderComponent
                setPanelMenu={setPanelMenu}
                setMenuItemsArray={setMenuItemsArray}
                providerUser={providerUser}
                logout={logout}
                user={user}
                menuItemsData={data}
                refetchMenuItems={refetch}
                loading={loading}
                error={error}
                menuItemsArray={menuItemsArray}
                panelMenu={panelMenu}
                setAuthCookie={setAuthCookie}
              />
              <WaterSystemSearchModal
                showSystemSelectModal={showSystemSelectModal}
                setShowSystemSelectModal={setShowSystemSelectModal}
                selectedWaterSystem={selectedWaterSystem}
                setSelectedWaterSystem={setSelectedWaterSystem}
              />
              <div className="waterlink-container">
                <div className="side-panel-container hidden">
                  {panelMenu !== "" && Array.isArray(menuItemsArray) && (() => {
                    const activeMenuItem = menuItemsArray.find(item => item.id === panelMenu);
                    return activeMenuItem && activeMenuItem.children ? (
                      <SidePanel
                        menuItems={activeMenuItem.children}
                        selectedWaterSystem={selectedWaterSystem}
                      />
                    ) : null;
                  })()}
                </div>
                <div className="main-content-container d-flex flex-column">
                  <Switch>
                    {/* PUBLIC ROUTES */}
                    {publicRoutes.map((entry) => (
                      <Route
                        key={entry.path}
                        exact
                        path={entry.path}
                      >
                        <entry.component
                          showSystemSelectModal={showSystemSelectModal}
                          setShowSystemSelectModal={setShowSystemSelectModal}
                          selectedWaterSystem={selectedWaterSystem}
                          setSelectedWaterSystem={setSelectedWaterSystem}
                        />
                      </Route>
                    ))}
                    {/*SECURE ROUTES*/}
                    {archiveRoutes.map((entry) => (
                      <PrivateRoute
                        key={entry.path}
                        exact
                        path={entry.path}
                        component={entry.component}
                        role={!user || !user.isPublic}
                      />
                    ))}
                    {ddwAdminRoutes.map((entry) => (
                      <PrivateRoute
                        key={entry.path}
                        exact
                        path={entry.path}
                        component={entry.component}
                        role={user && user.permissions?.includes("user_management")}
                      />
                    ))}
                    {enforcementRoutes.map((entry) => (
                      <PrivateRoute
                        key={entry.path}
                        exact
                        path={entry.path}
                        component={entry.component}
                        role={!user.isPublic}
                      />
                    ))}
                    {portalRoutes.map((entry) => (
                      <PrivateRoute
                        key={entry.path}
                        exact
                        path={entry.path}
                        component={entry.component}
                        role={user && user.permissions?.includes("portal_access")}
                      />
                    ))}
                    <Route component={URL404} />
                  </Switch>
                </div>
              </div>
              <StatusBar />
              <Footer />
            </Router>
          </HoverProvider>
        </WaterSystemSearchModalProvider>
      </UserContext.Provider>
    </EnvironmentProvider>
  );
}

export default App;
